import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { getRequest, postRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import {
  EMPTY_CRM_STATE_NAME,
  initialCrmStateNameFormValues,
  crmStateNamePayload,
  withCrmStateNameCopySuffix,
  normalizeCrmSetupApplicableTypes,
  crmSetupStateColorForPicker,
  crmSetupStateColorForStorage,
} from '../../utils/crmStateI18n';
import { ColorPicker } from 'primereact/colorpicker';
import { CRM_STATE_PHASE_OPTIONS } from '../../utils/crmStatePhases';
import { PanelMenu } from 'primereact/panelmenu';
import { MultiSelect } from 'primereact/multiselect';
import { createArrayNavigateHandlers } from '../../utils/arrayNavigate';
import { reloadCrmStatesCatalog } from '../../utils/crmCatalogStore';
import { SaveIcon, AddIcon, CopyIcon, PrintIcon, ExportIcon, RefreshIcon, VerticalLineIcon, SaveAndCloseIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon } from '../svg/svgIcons';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';

const TYPE_OPTIONS = [
  { labelKey: 'CrmState.Type.notice', value: 'notice' },
  { labelKey: 'CrmState.Type.acquisition', value: 'acquisition' },
  { labelKey: 'CrmState.Type.inquiry', value: 'inquiry' },
];
const PHASE_OPTIONS = CRM_STATE_PHASE_OPTIONS;
const TRANSITION_TYPE_OPTIONS = [
  { labelKey: 'ServiceState.TransitionType.NonFinal', value: 'non_final' },
  { labelKey: 'ServiceState.TransitionType.LevelFinal', value: 'level_final' },
  { labelKey: 'ServiceState.TransitionType.SectionFinal', value: 'section_final' },
  { labelKey: 'ServiceState.TransitionType.FinalManually', value: 'final_manually' },
  { labelKey: 'ServiceState.TransitionType.FinalAuto', value: 'final_auto' },
  { labelKey: 'ServiceState.TransitionType.Reopen', value: 'reopen' },
];

const PAGE_ADD_KEY = 'CustomerStatesAdd';
const MASK_ADD = 'cust009';

const normalizeTypes = (value) => normalizeCrmSetupApplicableTypes(value);

export default function CustomerStatesAdd({ crm_state, type = 'add' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const typeOptions = useMemo(
    () => TYPE_OPTIONS.map((opt) => ({ label: t(opt.labelKey), value: opt.value })),
    [t],
  );
  const { toast, openFavoriteDialog, setFastView, openChosenNotificationPreferences, setDialogText, setVisible, getSlovakColorName } = useContext(UtilContext);
  const [formType, setFormType] = useState(type);
  const [closeTab, setCloseTab] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const catalogRows = useMemo(
    () => (Array.isArray(state.crm_states) ? state.crm_states : []),
    [state.crm_states],
  );
  const submitRef = useRef(null);

  useEffect(() => {
    setFormType(type);
  }, [type]);

  const formik = useFormik({
    initialValues: {
      id: crm_state?.id || null,
      name: crm_state ? initialCrmStateNameFormValues(crm_state.name) : { ...EMPTY_CRM_STATE_NAME },
      applicable_types: normalizeTypes(crm_state?.applicable_types),
      color: crmSetupStateColorForPicker(crm_state?.color),
      phase: crm_state?.phase || '',
      transition_type: crm_state?.transition_type || '',
      sort_order: crm_state?.sort_order || 1,
      active: crm_state?.active ?? true,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!(values.name?.sk || '').trim()) errors['name.sk'] = t('Common.Required');
      if (!(values.name?.de || '').trim()) errors['name.de'] = t('Common.Required');
      if (!(values.name?.en || '').trim()) errors['name.en'] = t('Common.Required');
      if (!(values.phase || '').trim()) errors.phase = t('Common.Required');
      if (!(values.transition_type || '').trim()) errors.transition_type = t('Common.Required');
      return errors;
    },
    onSubmit: async (values) => {
      setDialogText?.(t('Common.Save'));
      setVisible?.(true);
      const payload = {
        name: crmStateNamePayload(values.name),
        applicable_types: normalizeTypes(values.applicable_types),
        color: crmSetupStateColorForStorage(values.color),
        phase: values.phase || '',
        transition_type: values.transition_type || '',
        active: !!values.active,
      };
      if (formType === 'edit') payload.sort_order = Number(values.sort_order || 1);
      try {
        if (formType === 'add') {
          const resp = await postRequest('/api/crm_states', payload, true);
          const created = resp?.data || null;
          if (created?.id) {
            formik.setValues({
              id: created.id,
              name: initialCrmStateNameFormValues(created.name),
              applicable_types: normalizeTypes(created.applicable_types),
              color: crmSetupStateColorForPicker(created.color || payload.color),
              phase: created.phase || payload.phase,
              transition_type: created.transition_type || payload.transition_type,
              sort_order: created.sort_order || payload.sort_order,
              active: created.active ?? payload.active,
            });
          }
          setFormType('edit');
          toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerStatesAdd.Toast.Saved'), life: 2500 });
        } else if (values.id) {
          await postRequest(`/api/crm_states/update/${values.id}`, payload, true);
          toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerStatesAdd.Toast.Updated'), life: 2500 });
        }
        await reloadCrmStatesCatalog(dispatch, getRequest).catch(() => {});
        if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('ServiceTask.SaveFailed'), life: 3000 });
      } finally {
        setVisible?.(false);
        setCloseTab(false);
      }
    },
  });

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = createArrayNavigateHandlers(catalogRows, formik.values?.id, (item) => {
    formik.setValues({
      id: item?.id || null,
      name: initialCrmStateNameFormValues(item?.name),
      applicable_types: normalizeTypes(item?.applicable_types),
      color: crmSetupStateColorForPicker(item?.color),
      phase: item?.phase || '',
      transition_type: item?.transition_type || '',
      sort_order: item?.sort_order || 1,
      active: item?.active ?? true,
    });
    setFormType('edit');
  });

  function openAddNew() {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerStatesAdd.New'), translationKey: 'CustomerStatesAdd.New', legacyLabel: t('CustomerStatesAdd.New'),
      page: PAGE_ADD_KEY,
      props: { crm_state: null, type: 'add' },
      mask: MASK_ADD,
    });
  }
  function copyCurrent() {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerStatesAdd.NewCopy'), translationKey: 'CustomerStatesAdd.NewCopy', legacyLabel: t('CustomerStatesAdd.NewCopy'),
      page: PAGE_ADD_KEY,
      props: { crm_state: { ...formik.values, id: null, name: withCrmStateNameCopySuffix(formik.values.name) }, type: 'add' },
      mask: MASK_ADD,
    });
  }

  const canAdd = !!user?.permissions?.includes('add customer_management-setup');
  const canEdit = !!user?.permissions?.includes('edit customer_management-setup');
  const nameFilled = !!(formik.values.name?.sk || '').trim()
    && !!(formik.values.name?.de || '').trim()
    && !!(formik.values.name?.en || '').trim();
  const disableSave = (formType === 'add' ? !canAdd : !canEdit)
    || !nameFilled
    || (formik.values.applicable_types || []).length < 1
    || !(formik.values.phase || '').trim()
    || !(formik.values.transition_type || '').trim();
  const navDisabled = formType !== 'edit' || !formik.values?.id || catalogRows.length < 2;
  const submitWithoutClose = () => {
    setCloseTab(false);
    submitRef.current?.click();
  };
  const submitWithClose = () => {
    setCloseTab(true);
    submitRef.current?.click();
  };
  const notifyUnavailable = (label) => {
    toast?.current?.show({ severity: 'warn', summary: t('Common.Info'), detail: t('Common.FeatureUnavailable', { label }), life: 2200 });
  };

  async function reloadCurrent() {
    if (!formik.values?.id) return;
    setDialogText?.(t('Common.LoadingData'));
    setVisible?.(true);
    try {
      const resp = await getRequest(`/api/crm_states/${formik.values.id}`);
      const row = resp?.data ?? null;
      if (row) {
        formik.setValues({
          id: row?.id || null,
          name: initialCrmStateNameFormValues(row?.name),
          applicable_types: normalizeTypes(row?.applicable_types),
          color: crmSetupStateColorForPicker(row?.color),
          phase: row?.phase || '',
          transition_type: row?.transition_type || '',
          sort_order: row?.sort_order || 1,
          active: row?.active ?? true,
        });
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('Common.DataReloaded'), life: 2500 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('Common.LoadFailed'), life: 3000 });
    } finally {
      setVisible?.(false);
    }
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.RefreshDetail'), icon: 'pi pi-refresh', command: () => reloadCurrent(), template: itemRenderer },
      { key: '0_2', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => openChosenNotificationPreferences?.(PAGE_ADD_KEY), template: itemRenderer },
    ],
  }];

  return (
    <form onSubmit={formik.handleSubmit} className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.SaveAndClose')} disabled={disableSave} className="disabled:opacity-25" onClick={submitWithClose}><SaveAndCloseIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.Save')} disabled={disableSave} className="disabled:opacity-25" onClick={submitWithoutClose}><SaveIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.AddNew')} disabled={!canAdd} className="disabled:opacity-25" onClick={openAddNew}><AddIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.Copy')} disabled={!canAdd || !nameFilled} className="disabled:opacity-25" onClick={copyCurrent}><CopyIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Print')} onClick={() => notifyUnavailable(t('Common.Print'))}><PrintIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Export')} onClick={() => notifyUnavailable(t('Common.Export'))}><ExportIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Refresh')} onClick={reloadCurrent}><RefreshIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollToFirst}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollUp}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollDown}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollToLast}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog?.(t('CustomerStatesList.FavoriteTitle'), PAGE_ADD_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView?.(PAGE_ADD_KEY)}><PriceIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]">
          <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
        </div>
      </div>
      <div className="relative flex flex-col h-[calc(100vh-177px)] overflow-y-auto overflow-x-hidden">
        <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
          <h1 className="text-3xl font-semibold text-left text-zinc-900">
            {t('CustomerStatesAdd.Title')}
          </h1>
          {formType === 'edit' && (
            <label className="block text-md font-semibold text-gray-800 mt-1">ID: {formik.values.id}</label>
          )}
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerStatesAdd.NameSk')}</label>
            <input
              type="text"
              name="name.sk"
              required
              className="block w-full px-4 py-2 mt-2 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none"
              value={formik.values.name?.sk ?? ''}
              onChange={formik.handleChange}
            />
            {formik.errors['name.sk'] && <div className="text-red-600 text-sm">{formik.errors['name.sk']}</div>}
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerStatesAdd.NameDe')}</label>
            <input
              type="text"
              name="name.de"
              required
              className="block w-full px-4 py-2 mt-2 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none"
              value={formik.values.name?.de ?? ''}
              onChange={formik.handleChange}
            />
            {formik.errors['name.de'] && <div className="text-red-600 text-sm">{formik.errors['name.de']}</div>}
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerStatesAdd.NameEn')}</label>
            <input
              type="text"
              name="name.en"
              required
              className="block w-full px-4 py-2 mt-2 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none"
              value={formik.values.name?.en ?? ''}
              onChange={formik.handleChange}
            />
            {formik.errors['name.en'] && <div className="text-red-600 text-sm">{formik.errors['name.en']}</div>}
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('ServiceStateAdd.ShowIn').toUpperCase()}</label>
            <MultiSelect
              required
              value={formik.values.applicable_types || []}
              onChange={(e) => formik.setFieldValue('applicable_types', normalizeTypes(e.value))}
              options={typeOptions}
              optionLabel="label"
              optionValue="value"
              filter
              display="chip"
              placeholder={t('ServiceStateAdd.SelectTypes')}
              className="w-full px-4 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
          </div>
          <div>
            <label htmlFor="color" className="block text-md font-semibold text-gray-800 mt-2">{t('ServiceStateAdd.StateColor').toUpperCase()}</label>
            <div className="mt-2 text-sm text-gray-800 flex items-center gap-2">
              <ColorPicker
                id="color"
                value={formik.values.color || ''}
                onChange={(e) => formik.setFieldValue('color', e.value)}
                className="mr-2"
              />
              {t('ServiceStateAdd.SelectedColor')}:{' '}
              <span className="font-semibold">#{formik.values.color || ''}</span>
              {formik.values.color ? (
                <>
                  {' '}
                  – <span>{getSlovakColorName?.(`#${formik.values.color}`) || ''}</span>
                </>
              ) : null}
            </div>
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('ServiceStateAdd.Phase').toUpperCase()}</label>
            <select
              className="block w-full px-4 py-2 mt-2 border rounded-md focus:border-gray-400"
              value={formik.values.phase}
              onChange={(e) => formik.setFieldValue('phase', e.target.value)}
            >
              <option value="">{t('Common.SelectOption')}</option>
              {PHASE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
            {formik.errors.phase && <div className="text-red-600 text-sm">{formik.errors.phase}</div>}
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('ServiceStateAdd.TransitionType').toUpperCase()}</label>
            <select
              className="block w-full px-4 py-2 mt-2 border rounded-md focus:border-gray-400"
              value={formik.values.transition_type}
              onChange={(e) => formik.setFieldValue('transition_type', e.target.value)}
            >
              <option value="">{t('Common.SelectOption')}</option>
              {TRANSITION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
            {formik.errors.transition_type && <div className="text-red-600 text-sm">{formik.errors.transition_type}</div>}
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('ServiceTaskTable.Col.State').toUpperCase()}</label>
            <select
              value={formik.values.active ? 'active' : 'inactive'}
              onChange={(e) => formik.setFieldValue('active', e.target.value === 'active')}
              className="block w-full px-4 py-2 mt-2 border rounded-md focus:border-gray-400"
            >
              <option value="active">{t('Common.Active')}</option>
              <option value="inactive">{t('Common.Inactive')}</option>
            </select>
          </div>
        </div>
      </div>
      <button type="submit" ref={submitRef} className="hidden" />
    </form>
  );
}

